/**
 * Markdown table generator — pure, deterministic GFM table formatting.
 * Safe to render server-side.
 *
 * Columns are padded to the widest cell, the separator row carries per-column
 * alignment markers (`:---`, `:--:`, `---:`), and pipes inside cells are
 * escaped. The first row is treated as the header (a blank header is synthesized
 * when `header` is false, since GFM tables always need a header row).
 */

export type Align = "left" | "center" | "right" | "none";

export interface MdTableOptions {
  align?: Align[];
  header?: boolean;
}

function escapeCell(s: string): string {
  return s.replace(/\|/g, "\\|").replace(/[\r\n]+/g, " ");
}

export function toMarkdownTable(
  rows: string[][],
  opts: MdTableOptions = {},
): string {
  if (!rows || rows.length === 0) return "";
  const { align = [], header = true } = opts;

  const numCols = Math.max(...rows.map((r) => r.length));
  if (numCols === 0) return "";

  const grid = rows.map((r) => {
    const out: string[] = [];
    for (let c = 0; c < numCols; c++) out.push(escapeCell(r[c] ?? ""));
    return out;
  });
  if (!header) grid.unshift(new Array(numCols).fill(""));

  const widths: number[] = [];
  for (let c = 0; c < numCols; c++) {
    let w = 0;
    for (const r of grid) w = Math.max(w, r[c].length);
    widths.push(Math.max(w, 3));
  }

  const alignOf = (c: number): Align => align[c] ?? "none";

  const padCell = (text: string, c: number): string => {
    const w = widths[c];
    const padTotal = w - text.length;
    const a = alignOf(c);
    if (a === "right") return " ".repeat(padTotal) + text;
    if (a === "center") {
      const left = Math.floor(padTotal / 2);
      return " ".repeat(left) + text + " ".repeat(padTotal - left);
    }
    return text + " ".repeat(padTotal);
  };

  const sepCell = (c: number): string => {
    const w = widths[c];
    const a = alignOf(c);
    if (a === "center") return `:${"-".repeat(w - 2)}:`;
    if (a === "left") return `:${"-".repeat(w - 1)}`;
    if (a === "right") return `${"-".repeat(w - 1)}:`;
    return "-".repeat(w);
  };

  const renderRow = (r: string[]): string =>
    `| ${r.map((cell, c) => padCell(cell, c)).join(" | ")} |`;

  const lines: string[] = [];
  lines.push(renderRow(grid[0]));
  lines.push(`| ${widths.map((_, c) => sepCell(c)).join(" | ")} |`);
  for (let i = 1; i < grid.length; i++) lines.push(renderRow(grid[i]));

  return lines.join("\n");
}
